import {
    VuexModule,
    Module,
    Mutation,
    Action,
    getModule,
} from 'vuex-module-decorators'
import store from '@/store'
import { UserModule } from './user'
import { ApiService } from '@/api/api.service'
import { getOperationID } from '@/api/operations'
import { getBoUrl } from '@/api/http-constants'
import orderBy from 'lodash/orderBy'

function filterMyGroups(groups, userID: number) {
    return groups.filter(group => {
        if (group.group.isPriority) {
            return false
        }

        for (let i = 0; i < group.users.length; i++) {
            if (group.users[i].id === userID) {
                return true
            }
        }
        return false
    })
}

function isMemberOfGroup(group, userID: number) {
    for (let i = 0; i < group.users.length; i++) {
        if (group.users[i].id === userID && group.users[i].state === 'member') {
            return true
        }
    }
    return false
}

interface SharedSpaceData {
    id: number
    name: string
    surface: number
    wishedCopros: number
    price: number
    member: boolean
}

interface TicketData {
    operation: {
        name: string
        image: string
    }
    appart: {
        surface: number
        exposure: 'south' | 'north' | 'west' | 'east'
        floor: 'ground' | 'first' | 'intermediate' | 'top'
        price: number
    }
    outdoor: {
        size: 'Aucun' | number | number[] // Peut être un tableau maintenant
        price: number
    }
    parking: {
        size: number // 0 | 1 | 2
        price: number
        boxed: boolean
    }
    sharedSpaces: SharedSpaceData[]
    priceTotal: number
}

export interface ITicketState {
    ticketData: TicketData | null
    phase: number
    planPdfUrl: string | null
    integratedPlanUrl: string | null
    sketches: string[]
    salePrice: number | null
    planRequested: boolean
}

@Module({ dynamic: true, store, name: 'Ticket' })
class Ticket extends VuexModule implements ITicketState {
    ticketData = null
    phase: number = 1
    planPdfUrl: string | null = null
    integratedPlanUrl: string | null = null
    sketches: string[] = []
    salePrice: number | null = null
    planRequested: boolean = false

    loading: boolean = false

    @Mutation
    public setLoading(loading: boolean) {
        this.loading = loading
    }

    @Mutation
    public setTicketData(ticketData: TicketData | null) {
        this.ticketData = ticketData
    }

    @Mutation
    public setPhase(phase: number) {
        this.phase = phase
    }

    @Mutation
    public setPlanPdfUrl(url: string | null) {
        this.planPdfUrl = url
    }

    @Mutation
    public setIntegratedPlanUrl(url: string | null) {
        this.integratedPlanUrl = url
    }

    @Mutation
    public setSketches(sketches: string[]) {
        this.sketches = sketches
    }

    @Mutation
    public setSalePrice(price: number | null) {
        this.salePrice = price
    }

    @Mutation
    public setPlanRequested(val: boolean) {
        this.planRequested = val
    }

    @Action({ rawError: true })
    private async calcul() {
        const apiService = new ApiService()

        await UserModule.Init()
        const userID = UserModule.userInfos.id
        const opeID = await getOperationID()

        const opeResp = await apiService.getOperation(opeID)
        const ope = opeResp.data

        const userOpeResp = await apiService.getUserOperation(userID, opeID)
        const userOpe = userOpeResp.data

        // Phase de l'utilisateur (stockée sur userOpe, pas sur ope)
        this.context.commit('setPhase', (userOpe as any).phase || 1)

        // URL PDF du plan générique déposé par l'admin (phase 2)
        // Plan Intégré déposé par l'admin (phase 3) — même mécanisme, même blueprints array
        // On distingue par blueprintType : 'generic' (phase 2) vs 'integrated' (phase 3)
        const boBase = getBoUrl().replace(/\/$/, '')
        const adminBps = ((userOpe as any).blueprints || []).filter((p: any) => p.isUploadedByAdmin)
        const userBps = ((userOpe as any).blueprints || []).filter((p: any) => !p.isUploadedByAdmin)
        // planRequested = true si l'utilisateur a validé son projet (plan_validated = true en BD)
        // OU s'il a déjà envoyé un blueprint
        this.context.commit('setPlanRequested', !!(userOpe as any).planValidated || userBps.length > 0)

        const genericBps = adminBps.filter((p: any) => !p.blueprintType || p.blueprintType === 'generic')
        if (genericBps.length) {
            const latest = genericBps.reduce((prev: any, curr: any) =>
                new Date(prev.createdAt) < new Date(curr.createdAt) ? curr : prev)
            this.context.commit('setPlanPdfUrl', boBase + latest.filePath)
        } else {
            this.context.commit('setPlanPdfUrl', null)
        }

        const integratedBps = adminBps.filter((p: any) => p.blueprintType === 'integrated')
        if (integratedBps.length) {
            const latest = integratedBps.reduce((prev: any, curr: any) =>
                new Date(prev.createdAt) < new Date(curr.createdAt) ? curr : prev)
            this.context.commit('setIntegratedPlanUrl', boBase + latest.filePath)
        } else {
            this.context.commit('setIntegratedPlanUrl', null)
        }

        // Esquisses architecturales communes à tous (stockées sur l'opération)
        const sketchPaths: string[] = (ope as any).sketches || []
        this.context.commit('setSketches', sketchPaths.map(p => boBase + p))

        // Prix de vente saisi manuellement par l'admin (propre au userOpe)
        this.context.commit('setSalePrice', (userOpe as any).proposedPrice || null)

        const groupsResp = await apiService.getGroupsByOperation(opeID)
        const allGroups = groupsResp.data
        const myGroups = filterMyGroups(allGroups, userID)

        const parametersResp = await apiService.getParameters()
        const _parameters = parametersResp.data

        const parameters = _parameters.reduce((accu, parameter) => {
            accu[parameter.slug] = parameter.value
            return accu
        }, {})

        if (userOpe.favorite) {
            const favoriteStr = userOpe.favorite
            try {
                const favorite = JSON.parse(favoriteStr)

                const keys = Object.keys(favorite)
                if (keys.length === 0) {
                    this.context.commit('setTicketData', null)
                    return
                }

                const parkingSize = parseInt(userOpe.wishedParking, 10)
                const parkingBoxed = userOpe.wishedParkingBoxed
                const parkingPrice = parkingSize * (parkingBoxed ? ope.parkingBoxedPrice : ope.parkingPrice)

                const outdoorPrice = (() => {
                    // Vérifie si wishedOutdoorSurfaces existe et est un tableau
                    if (userOpe.wishedOutdoorSurfaces && Array.isArray(userOpe.wishedOutdoorSurfaces) && userOpe.wishedOutdoorSurfaces.length > 0) {
                        const totalSurface = userOpe.wishedOutdoorSurfaces.reduce((sum, surface) => sum + surface, 0)

                        if (favorite.floor === 'ground' || favorite.floor === 'house') {
                            return totalSurface * ope.outdoorGroundPrice
                        }
                        return totalSurface * ope.outdoorFloorPrice
                    }
                    // Compatibilité avec l'ancien système (wishedOutdoorFloor)
                    else if (!isNaN(userOpe.wishedOutdoorFloor)) {
                        if (favorite.floor === 'ground' || favorite.floor === 'house') {
                            return (
                                userOpe.wishedOutdoorFloor *
                                ope.outdoorGroundPrice
                            )
                        }

                        return (
                            userOpe.wishedOutdoorFloor *
                            ope.outdoorFloorPrice
                        )
                    } else {
                        return 0
                    }
                })()

                const sharedSpaces: SharedSpaceData[] = myGroups.map(group => {
                    const surface = group.group.surface
                    const wishedCopros = group.group.wishedCopros
                    const surfacePrice = group.group.isExterior ? ope.outdoorFloorPrice : ope.mediumPrice
                    const price = Math.ceil(
                        (surface * surfacePrice) / wishedCopros
                    )

                    const isMember = isMemberOfGroup(group, userID)

                    const sharedSpace: SharedSpaceData = {
                        id: group.group.id,
                        name: group.group.sharedSpace.name,
                        surface,
                        wishedCopros,
                        price,
                        member: isMember,
                    }
                    return sharedSpace
                })

                const orderedSharedSpaces = orderBy(
                    sharedSpaces,
                    ['member'],
                    ['desc']
                )

                const priceTotal = (() => {
                    const sharedSpacesTotal = sharedSpaces.reduce(
                        (accu, sharedSpace) => {
                            return accu + sharedSpace.price
                        },
                        0
                    )

                    return (
                        favorite.price +
                        outdoorPrice +
                        parkingPrice +
                        sharedSpacesTotal
                    )
                })()

                const ticketData: TicketData = {
                    operation: {
                        name: ope.name,
                        image: `${getBoUrl()}${ope.imagePath}?t=${new Date().valueOf()}`,
                    },
                    appart: {
                        ...favorite,
                    },
                    outdoor: {
                        size: userOpe.wishedOutdoorSurfaces && userOpe.wishedOutdoorSurfaces.length > 0
                            ? userOpe.wishedOutdoorSurfaces
                            : userOpe.wishedOutdoorFloor,
                        price: outdoorPrice,
                    },
                    parking: {
                        size: parkingSize,
                        price: parkingPrice,
                        boxed: parkingBoxed
                    },
                    sharedSpaces: orderedSharedSpaces,
                    priceTotal,
                }

                // console.log(ticketData)
                this.context.commit('setTicketData', ticketData)
            } catch (e) {
                console.log(e)
            }
        }

        // console.log('ope', ope)
        // console.log('userOpe', userOpe)
        // console.log('allGroups', allGroups)
        // console.log('myGroups', myGroups)
        // console.log('parameters', parameters)
    }

    @Action({})
    public async refresh() {
        if (this.loading) {
            return
        }

        this.context.commit('setLoading', true)
        try {
            await this.calcul()
        } finally {
            this.context.commit('setLoading', false)
        }
    }
}

export const TicketModule = getModule(Ticket)
